import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { billingAPI } from '../../services/api';
import { INDIAN_STATES } from '../../utils/helpers';

// Who our GST tax invoices for plan payments are made out to. Taken when an invoice is issued, so a change
// here applies to invoices issued from then on — an invoice that already exists is never rewritten.
//
// Name, address and state are REQUIRED before any plan can be bought (the server refuses payment without them).
// The GST number is optional; when one is entered the state is taken from its first two digits and locked,
// because for a registered business that state decides CGST + SGST versus IGST.
export function BillingDetailsForm({ onSaved, submitLabel = 'Save billing details' }) {
  const [form, setForm] = useState({ name: '', gstin: '', address: '', state: '' });
  const [meta, setMeta] = useState({ defaultName: '', sellerState: '', gstStateCodes: {} });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    billingAPI.getBillingDetails()
      .then(({ data }) => {
        // Pre-fill the name with the company name so most people only have to add an address and state.
        setForm({ ...data.billingDetails, name: data.billingDetails.name || data.defaultName || '' });
        setMeta({ defaultName: data.defaultName, sellerState: data.sellerState, gstStateCodes: data.gstStateCodes || {} });
        setLoaded(true);
      })
      .catch((err) => toast.error(err.response?.data?.message || 'Failed to load billing details'));
  }, []);

  const set = (key) => (e) => { setError(''); setForm({ ...form, [key]: e.target.value }); };

  // A GST number carries its state in the first two digits (that is the place of supply), so the state follows it.
  const stateFromGstin = meta.gstStateCodes[form.gstin.slice(0, 2)] || '';
  const shownState = stateFromGstin || form.state;
  const onGstinChange = (e) => {
    setError('');
    const gstin = e.target.value.toUpperCase();
    setForm({ ...form, gstin, state: meta.gstStateCodes[gstin.slice(0, 2)] || form.state });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError('Enter the name for the invoice.');
    if (!form.address.trim()) return setError('Enter your billing address.');
    if (!shownState) return setError('Choose your state.');
    setSaving(true);
    try {
      const { data } = await billingAPI.updateBillingDetails({ ...form, state: shownState });
      setForm(data.billingDetails);
      toast.success('Billing details saved');
      if (onSaved) onSaved(data.billingDetails);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save billing details');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <div className="muted" style={{ fontSize: 13 }}>Loading…</div>;

  return (
    <form onSubmit={save}>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Name on invoice *</label>
          <input className="form-control" value={form.name} onChange={set('name')} placeholder={meta.defaultName} maxLength={200} />
        </div>
        <div className="form-group">
          <label className="form-label">GST number (optional)</label>
          <input className="form-control" value={form.gstin} onChange={onGstinChange} placeholder="37ABCDE1234F1Z5" maxLength={15} />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Billing address *</label>
        <textarea className="form-control" rows={2} value={form.address} onChange={set('address')} maxLength={500} />
      </div>
      <div className="form-group">
        <label className="form-label">State *</label>
        <select className="form-control" value={shownState} onChange={set('state')} disabled={!!stateFromGstin}>
          <option value="">Select state…</option>
          {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          {stateFromGstin ? 'Set automatically from your GST number — a registered business is taxed by the state on its GST number. ' : ''}
          {meta.sellerState ? `Customers in ${meta.sellerState} are charged CGST + SGST; other states are charged IGST.` : ''}
        </div>
      </div>
      {error && <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 10 }}>{error}</div>}
      <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : submitLabel}</button>
    </form>
  );
}

export default function BillingDetailsCard() {
  return (
    <>
      <div className="card-title" style={{ marginBottom: 8, fontSize: 14 }}>Billing details for GST invoices</div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
          Every successful plan payment gets a GST tax invoice, and these details are required before you can buy a plan.
          Add them <strong>before you pay</strong> — an invoice that is already issued is not changed afterwards.
        </div>
        <BillingDetailsForm />
      </div>
    </>
  );
}
